import {isString, isUndefined, pick} from 'lodash';
import React, {FC, ReactNode, useEffect, useMemo} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  useDeepMemo,
  useLazyUnmountCancelToken,
} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {AsyncStatusesEnum} from '../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {isDefined} from '../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {ConversationSortPropertiesEnum} from '../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {useAppQuery} from '../../../../../libs/shared/core/src/services/query/useAppQuery';
import {getHasSearchQuerySuggestions} from '../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {CellsContext} from '../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {
  CellsPanelId,
  findInboxesPanelIdInPanelId,
} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {InlineLinkButton} from '../../../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {Loader} from '../../../../../libs/shared/ui/src/components/loaders/loader';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {DEFAULT_SEARCH_FILTERS} from '../../../../../srcCommon/inboxes/search/searchFiltersHelpers';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {findThemedImageInfo, ThemedImageNamesEnum} from '../../../../helpers/imageThemeHelpers';
import {InboxesSearchLocationsEnum} from '../../../../models/url/models/inboxesUrlModel';
import {fetchConversationsCountAction} from '../../../../store/panels/cells/cellsPanelActions';
import {buildSearchParameters} from '../../../../store/panels/cells/helpers/cellsPanelPathHelpers';
import {
  getCellsSearchQueryHash,
  getCellsSearchResultsCounts,
} from '../../../../store/panels/cells/search/cellsPanelSearchSelectors';
import {CellsPanelSearchAppliedQuerySuggestion} from '../../../../store/panels/cells/search/cellsPanelSearchState';
import {toggleSearchQueryBuilderDropdownAction} from '../../../../store/panels/inboxes/inboxesPanelActions';
import {
  InboxesConversationsParametersWithSearch,
  InboxesSearchParameters,
} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {SearchInteractionOptions} from '../../../../store/stateless/searchActions';
import {makeGetSearchLocalFilterName} from '../hints/searchHintsBar';
import {trackSearchEmptyStateView, trackSearchNoResultsNudgeClick} from '../searchTrackingHelpers';
import {SearchNoResultsHeader} from './searchNoResultsHeader';
import {searchNoResultsQueries} from './searchNoResultsQueries';

/*
 * Constants.
 */

export const SEARCH_NO_RESULTS_MAX_CHARS_QUERY = 20;
const MAX_CHARS_LOCATION = 40;

/*
 * Props.
 */

export interface SearchNoResultsMessageProps {
  panelId: CellsPanelId;
  parameters: InboxesConversationsParametersWithSearch;
  cellsContext: CellsContext;
  navigateToSearch: (
    searchParameters: InboxesSearchParameters,
    cellsContext?: CellsContext,
    options?: SearchInteractionOptions,
    sortProperty?: ConversationSortPropertiesEnum,
    appliedQuerySuggestion?: CellsPanelSearchAppliedQuerySuggestion,
  ) => void;
}

/*
 * Styles.
 */

export const StyledNoResultsWrapperDiv = styled.div`
  max-width: 480px;
`;

export const StyledNoResultsFooterDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const StyledNoResultsFooterContentDiv = styled.div`
  margin-top: 8px;
  margin-bottom: 16px;

  white-space: normal;

  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  text-align: center;
  line-height: 21px;
`;

/*
 * Component.
 */

export const SearchNoResultsMessage: FC<SearchNoResultsMessageProps> = (props) => {
  const {panelId, parameters, cellsContext, navigateToSearch} = props;
  const {
    search: {query, location: currentSearchLocation},
  } = parameters;

  // TODO: Use React Query.
  const {isLoading: areLocationSuggestionsLoading, locationSuggestion} = useSuggestedSearchLocation(
    parameters,
    cellsContext,
  );

  const searchQueryParameters = useAppSelector((state) => buildSearchParameters(state, parameters));

  const shouldFetchQuerySuggestions = useAppSelector(getHasSearchQuerySuggestions);
  const {data: querySuggestions, asyncStatus: querySuggestionsStatus} = useAppQuery(
    searchNoResultsQueries.querySuggestions(
      parameters,
      cellsContext,
      searchQueryParameters,
      shouldFetchQuerySuggestions,
    ),
  );
  const areQuerySuggestionsLoading = querySuggestionsStatus === AsyncStatusesEnum.LOADING;

  const getSearchLocalFilterName = useMemo(() => makeGetSearchLocalFilterName(parameters), [parameters]);
  const localSearchFilterName = useAppSelector(getSearchLocalFilterName);

  const queryHash = useAppSelector(getCellsSearchQueryHash);
  const dispatch = useAppDispatch();
  const theme = useUserTheme();

  const inboxesPanelId = findInboxesPanelIdInPanelId(panelId);
  const toggleSearchQueryBuilderDropdown = () =>
    // Toggle query builder
    dispatch(toggleSearchQueryBuilderDropdownAction(inboxesPanelId, 'no-results-helpful-tip'));

  const firstQuerySuggestion = querySuggestions?.searchTermCorrections[0];
  const hasQuerySuggestion = firstQuerySuggestion && !areQuerySuggestionsLoading;

  const shouldAutomaticallyApplyQuerySuggestion =
    !areLocationSuggestionsLoading && !locationSuggestion && hasQuerySuggestion;

  useEffect(() => {
    if (!shouldAutomaticallyApplyQuerySuggestion) {
      return;
    }

    // Automatically apply suggestion.
    navigateToSearch(
      {...parameters.search, query: firstQuerySuggestion.text},
      cellsContext,
      {
        isFolderSwitch: false,
      },
      undefined,
      {
        originalQuery: parameters.search.query,
        originalQueryNormalTerms: querySuggestions.originalQueryNormalTerms,
        suggestedQueryNormalTerms: firstQuerySuggestion.textNormalTerms,
      },
    );
  }, [
    cellsContext,
    navigateToSearch,
    parameters.search,
    querySuggestions,
    shouldAutomaticallyApplyQuerySuggestion,
    query,
    firstQuerySuggestion,
  ]);

  // Show location suggestion as soon as possible since we know we won't apply the query correction if it exists.
  const shouldWaitForQuerySuggestions =
    !areLocationSuggestionsLoading && !locationSuggestion && areQuerySuggestionsLoading;

  const shouldShowLoader =
    areLocationSuggestionsLoading ||
    shouldWaitForQuerySuggestions ||
    // Don't display the no-results text for a split second while we redirect the user to the corrected query results.
    shouldAutomaticallyApplyQuerySuggestion;

  useEffect(() => {
    if (shouldShowLoader || !queryHash) {
      return;
    }

    const variant = isDefined(locationSuggestion) ? 'other_location' : 'no_results';
    trackSearchEmptyStateView({queryHash, variant});
  }, [locationSuggestion, queryHash, shouldShowLoader]);

  if (shouldShowLoader) {
    return <Loader />;
  }

  const searchNoResultsAnywhere = findThemedImageInfo(theme, ThemedImageNamesEnum.SEARCH_NO_RESULTS_ANYWHERE);

  // Render a helpful tip if we're sure we won't find any results.
  if (!locationSuggestion) {
    return (
      <StyledNoResultsWrapperDiv>
        <SearchNoResultsHeader
          image={searchNoResultsAnywhere.src}
          imageWidth={searchNoResultsAnywhere.width}
          imageHeight={searchNoResultsAnywhere.height}
        >
          <Trans id="search-no-results-helpful-tip-header">
            Didn&apos;t find {maybeTruncateAndAddQuotes(query, SEARCH_NO_RESULTS_MAX_CHARS_QUERY, true)}{' '}
            anywhere
          </Trans>
        </SearchNoResultsHeader>
        <StyledNoResultsFooterDiv>
          <StyledNoResultsFooterContentDiv>
            <Trans id="search-no-results-helpful-tip-footer">
              Use the{' '}
              <InlineLinkButton
                interactionId="search-no-results-helpful-tip-cta"
                onClick={toggleSearchQueryBuilderDropdown}
              >
                search builder
              </InlineLinkButton>{' '}
              to try another search.
            </Trans>
          </StyledNoResultsFooterContentDiv>
        </StyledNoResultsFooterDiv>
      </StyledNoResultsWrapperDiv>
    );
  }

  const headerTitle = (
    <Trans id="search-no-results-nudge-header">
      Didn&apos;t find {maybeTruncateAndAddQuotes(query, SEARCH_NO_RESULTS_MAX_CHARS_QUERY, true)} in{' '}
      {renderLocationLabel(currentSearchLocation, localSearchFilterName)}
    </Trans>
  );

  const renderFooter = () => {
    const onCtaClick = () => {
      // Track.
      trackSearchNoResultsNudgeClick({
        queryHash,
        currentLocation: parameters.search.location,
        suggestedLocation: locationSuggestion,
      });

      // Navigate.
      navigateToSearch({...parameters.search, location: locationSuggestion}, cellsContext, {
        isFolderSwitch: true,
      });
    };

    const footerText =
      locationSuggestion === InboxesSearchLocationsEnum.PINNED_IN_WORKSPACE ? (
        <Trans id="search-no-results-nudge-footer-sidebar">
          Searching your sidebar, we found matching results.
        </Trans>
      ) : (
        <Trans id="search-no-results-nudge-footer-global">
          Searching all inboxes, we found matching results.
        </Trans>
      );

    return (
      <StyledNoResultsFooterDiv>
        <StyledNoResultsFooterContentDiv>{footerText}</StyledNoResultsFooterContentDiv>
        <Button
          interactionId="search-no-results-nudge-cta"
          onClick={onCtaClick}
          variant={ButtonVariantsEnum.PRIMARY}
          size={VisualSizesEnum.MEDIUM}
        >
          <Trans id="search-no-results-nudge-cta">View results</Trans>
        </Button>
      </StyledNoResultsFooterDiv>
    );
  };

  return (
    <StyledNoResultsWrapperDiv>
      <SearchNoResultsHeader>{headerTitle}</SearchNoResultsHeader>
      {renderFooter()}
    </StyledNoResultsWrapperDiv>
  );
};

/*
 * Helpers.
 */

function useSuggestedSearchLocation(
  parameters: InboxesConversationsParametersWithSearch,
  cellsContext: CellsContext,
) {
  const stableParameters = useDeepMemo(() => parameters, [parameters]);
  const stableCellsContext = useDeepMemo(() => cellsContext, [cellsContext]);

  const dispatch = useAppDispatch();

  const cellsSearchResultsCounts = useAppSelector(getCellsSearchResultsCounts);

  // Compute locations for which we need to check if results exist.
  const availableLocations = useMemo(() => {
    const currentLocationIndex = DEFAULT_SEARCH_FILTERS.indexOf(stableParameters.search.location);
    return DEFAULT_SEARCH_FILTERS.slice(currentLocationIndex + 1);
  }, [stableParameters.search.location]);

  const availableLocationCounts = pick(cellsSearchResultsCounts, availableLocations);

  const buildUnmountCancelToken = useLazyUnmountCancelToken();

  // For each available location, fetch the number of conversations.
  useEffect(() => {
    availableLocations.forEach((location) =>
      dispatch(
        fetchConversationsCountAction(
          stableParameters,
          location,
          stableCellsContext,
          buildUnmountCancelToken(),
        ),
      ),
    );
  }, [
    availableLocations,
    buildUnmountCancelToken,
    dispatch,
    stableCellsContext,
    // `stableParameters.selection` changes from an empty array to [0].
    // If we were to use `stableParameters` as-is in the dependencies, we would double the number of API calls.
    // We only need to retrieve counts whenever `stableParameters.search` changes.
    stableParameters.search,
  ]);

  return {
    // Wait for all counts to be defined.
    isLoading: Object.values(availableLocationCounts).some(isUndefined),
    // Take the first available location that has results.
    locationSuggestion: availableLocations.find((location) => {
      const totalForLocation = availableLocationCounts[location];
      return isDefined(totalForLocation) && totalForLocation > 0;
    }),
  };
}

export function maybeTruncateAndAddQuotes(
  value: string,
  maxLength: number,
  shouldAddQuotes?: boolean,
): ReactNode {
  if (!value) {
    return null;
  }

  if (value.length <= maxLength) {
    return maybeAddQuotes(value, shouldAddQuotes);
  }

  return (
    <TooltipCoordinator isInline render={() => maybeAddQuotes(value, shouldAddQuotes)}>
      {maybeAddQuotes(`${value.slice(0, maxLength)}…`, shouldAddQuotes)}
    </TooltipCoordinator>
  );
}

function maybeAddQuotes(value: string, shouldAddQuotes?: boolean) {
  if (!shouldAddQuotes) {
    return value;
  }

  return `“${value}“`;
}

function renderLocationLabel(
  location: InboxesSearchLocationsEnum,
  localSearchFilterName?: ReactNode,
): ReactNode {
  switch (location) {
    case InboxesSearchLocationsEnum.LOCAL:
      return isString(localSearchFilterName)
        ? maybeTruncateAndAddQuotes(localSearchFilterName, MAX_CHARS_LOCATION)
        : localSearchFilterName;

    case InboxesSearchLocationsEnum.PINNED_IN_WORKSPACE:
      return <Trans id="search-no-results-your-sidebar">your sidebar</Trans>;

    default:
      return null;
  }
}
